import styles from './order-view.module.scss';
import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import {
  Order,
  OrderDocumentType,
  OrderPaymentMethod,
  OrderReturnStatus,
  OrderSource,
  OrderStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import classNames from 'classnames';
import { Button } from '../../../components/form/button';
import { StickyHeader } from '../../../components/sticky-header/sticky-header';
import { OrderSection } from '../order-section/order-section';
import { Typography } from '../../../components/typography/typography';
import { Collapsible } from '../../../components/collapsible/collapsible';
import Icon from '../../../components/icons/icon';
import { CustomerInfo } from '../create/customer-info';
import { User, UserAddress } from '../../../clients/quipli-service/user/user';
import { Spinner } from '../../../components/spinner/spinner';
import { DataTable, DataTableColumn } from '../../../components/table/data-table';
import { Location } from '../../../clients/quipli-service/location/location';
import { toast } from '../../../util/toasts';
import { useAdmin } from '../../../hooks/use-admin-context';
import { OrderInvoices } from '../invoices/order-invoices-v2';
import { EmailHistory } from '../email-history/email-history';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { DateTime, Duration } from 'luxon';
import { useNavigate, useParams } from 'react-router-dom';
import { StatusTransitionButtonsV2 } from '../status-transitions/status-transition-button-v2';
import { OrderSecurityDeposit } from '../payments/order-security-deposit-v2';
import { OrderContract } from '../order-contract/order-contract-v2';
import { addNumericSuffix } from '../../../util/util';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { TenantContext } from '../../../context/tenant-context';
import {
  CashPaymentMethod,
  CheckPaymentMethod,
  CustomPaymentMethod,
  OnAccountPaymentMethodRequest,
  PaymentMethodRequest,
  PaymentMethodType,
  StripeCard,
  StripePaymentMethodRequest,
} from '../../../clients/quipli-service/payment/payment';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { Badge } from '../../../components/badge/badge';
import { OnAccount, OnAccountStatus } from '../../../clients/quipli-service/payment/on-account/on-account';
import { OrderPaymentMethodSelector } from '../../payment/payment-method-selector';
import { isMobileOnly } from 'react-device-detect';
import { OrderMediaList } from './order-media-list';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { QboInvoice } from '../../qbo/invoice/qbo-invoice-v2';
import { OrderNotes } from './order-notes';
import { UserAgree } from '../user-agree/user-agree-v2';
import { DefaultInvoiceFreeText } from './default-invoice-free-text';
import { LocationSelect } from '../../location/select/location-select';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { Field } from '../../../components/field/field';
import { isDeliveryTripRequired } from '../../../clients/quipli-service/delivery/delivery';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { SelectForm } from '../../../components/select-form/select-form';
import { PaymentContextProvider } from '../../../context/payment-context';
import { SeisDamageWaiversModal } from '../seis-damage-waivers/seis-damage-waivers';
import { Dictionary, keyBy } from 'lodash';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { JobType } from '../../../clients/quipli-service/job-tasks/job-tasks';
import { OrderHistory } from '../order-history/order-history';
import { Representative } from '../../../clients/quipli-service/representatives/representative';
import { InspectionView } from '../../job-tasks/inspection/inspection-view';

export const OrderViewPage = () => {
  const { orderId } = useParams();
  return <OrderView orderId={parseInt(orderId!)} />;
};

export const OrderView = ({ orderId }: { orderId: number }) => {
  const { canEditOrder, updateCustomContractInvoiceText, updateOrderSalesRep } = useOrderApiV1();
  const { getOrderById, getLocationById, getMultipleConfigurations } = useAPI();
  const { contextPath } = useContext(TenantContext);
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order>();
  const [user, setUser] = useState<User | null>(null);
  const [author, setAuthor] = useState<Admin>();
  const [location, setLocation] = useState<Location | null>();
  const [isQuote, setIsQuote] = useState<boolean>(false);
  const [representative, setRepresentative] = useState<Representative>();
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [deliveryAddress, setDeliveryAddress] = useState<UserAddress>();
  const [billingAddress, setBillingAddress] = useState<UserAddress>();
  const [configs, setConfigs] = useState<Dictionary<Configuration>>();
  const [showTaxInclusive, setShowTaxInclusive] = useState<boolean>(false);

  const fetchOrder = async (): Promise<Order> => {
    let _order: Order;
    try {
      _order = await getOrderById(orderId);
      const { isValid } = await canEditOrder(_order.id);
      setIsEditable(isValid);
    } catch (error: any) {
      toast(typeof error.message === 'string' ? error.message : 'Invalid record.', 'error');
      navigate(`${contextPath}manager/orders`);
    }

    setOrder(_order!);
    setIsQuote(_order!.status === OrderStatus.Quote);
    setRepresentative(_order!.authorizedRepresentative);

    return _order!;
  };

  useEffect(() => {
    if (order && !isEditMode) {
      setBillingAddress(order.billingAddress);
      setDeliveryAddress(order.shippingAddress);
    }
  }, [isEditMode, order]);

  useEffect(() => {
    const fetchAll = async () => {
      const _order = await fetchOrder();
      const _location = await getLocationById(_order.locationId);

      setLocation(_location);
      setUser(_order.user);
      setAuthor(_order.admin);
    };

    fetchAll();
  }, [orderId]);

  useEffect(() => {
    getMultipleConfigurations([
      'CONF_COMMISSION_ENABLED',
      'CONF_SEIS_INTEGRATION',
      'CONF_INSPECTIONS_ENABLED',
      'CONF_UNIT_TRACKING_VIA',
      'CONF_FUEL_UNIT',
    ]).then((configs) => {
      setConfigs(keyBy(configs, 'variable'));
    });
  }, []);

  return !order || !location || !configs ? (
    <Spinner />
  ) : (
    <div className="px-[10px] bg-gray-100">
      {/* Work flow */}
      <StickyHeader
        title={
          <>
            <span>
              {isQuote ? 'Quote' : order.documentType == OrderDocumentType.CreditMemo ? 'Credit Memo' : 'Order'}
            </span>
            <span className="text-grey-500">({order.orderNumber})</span>
          </>
        }
      >
        <StatusTransitionButtonsV2
          order={order}
          location={location}
          isDisabled={isEditMode}
          reload={fetchOrder}
          hideTransitions={
            order.documentType == OrderDocumentType.CreditMemo
              ? ['on-rent', 'off-rent', 'quote', 'inventory-return', 'reopen', 'missed']
              : undefined
          }
        />
      </StickyHeader>

      <div className={classNames('grid grid-cols-1 lg:grid-cols-3 gap-y-[15px] lg:gap-y-0 lg:gap-x-[15px] mt-[15px]')}>
        <div className="col-span-2 flex flex-col gap-y-[15px]">
          <OrderStatusView order={order} location={location} isQuote={isQuote} />

          <OrderDetails
            order={order}
            isQuote={isQuote}
            location={location}
            author={author}
            isDisabled={isEditMode}
            refresh={fetchOrder}
          />

          {configs['CONF_INSPECTIONS_ENABLED']?.value === '1' && order.documentType == OrderDocumentType.Order && (
            <>
              <InspectionView
                order={order}
                jobType={JobType.CheckoutInspection}
                fetchOrder={fetchOrder}
                unitTrackingVia={configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber'}
                fuelUnit={configs['CONF_FUEL_UNIT']?.value as 'gallons' | 'liters'}
              />
              <InspectionView
                order={order}
                jobType={JobType.CheckinInspection}
                fetchOrder={fetchOrder}
                unitTrackingVia={configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber'}
                fuelUnit={configs['CONF_FUEL_UNIT']?.value as 'gallons' | 'liters'}
              />
            </>
          )}

          {order.isCycleBilled && <OrderCycleBillingDetails order={order} reload={fetchOrder} />}

          <OrderContract
            order={order}
            location={location}
            canEdit={isEditable}
            refresh={fetchOrder}
            isEditMode={isEditMode}
            setEditMode={setIsEditMode}
            deliveryAddress={deliveryAddress}
            billingAddress={billingAddress}
            showTaxInclusive={showTaxInclusive}
            setShowTaxInclusive={setShowTaxInclusive}
          />

          <OrderInvoices order={order} refresh={fetchOrder} />

          <OrderSecurityDeposit order={order} location={location} refresh={fetchOrder} />

          <EmailHistory
            orderId={order.id.toString()}
            orderType="Order"
            isQuote={order.status === OrderStatus.Quote}
            orderDateAdded={order.dateAdded}
          />

          <QboInvoice orderId={order.id} />

          {configs['CONF_SEIS_INTEGRATION']?.value === '1' && order.documentType == OrderDocumentType.Order && (
            <SeisDamageWaiversModal order={order} location={location} />
          )}
        </div>

        <div className="flex flex-col gap-y-[15px]">
          {!!user && !!order && (
            <>
              <OrderSection
                className="flex flex-col gap-[8px]"
                header={
                  <div className="flex justify-between items-center">
                    <Typography type="text-md" weight="medium" className="text-grey-800">
                      Location
                    </Typography>
                  </div>
                }
              >
                <LocationSelect
                  orderLocation={location}
                  setLocation={setLocation}
                  dataCy="location-select"
                  isEditMode={isEditMode}
                />
              </OrderSection>

              <CustomerInfo
                isCreateMode={false}
                customer={user}
                setCustomer={setUser}
                billing={billingAddress}
                setBilling={setBillingAddress}
                delivery={deliveryAddress}
                setDelivery={setDeliveryAddress}
                representative={representative}
                setRepresentative={setRepresentative}
                isEditMode={isEditMode}
                orderId={order.id}
              />

              <OrderNotes order={order} refresh={fetchOrder} />

              {configs['CONF_COMMISSION_ENABLED']?.value === '1' && order.documentType == OrderDocumentType.Order && (
                <AssignSalesRep
                  salesRep={order.salesRep?.adminId}
                  updateSalesRep={(rep?: number) => {
                    updateOrderSalesRep(orderId, rep)
                      .then(() => {
                        toast('Updated sales rep');
                      })
                      .catch((e: any) => {
                        console.error(e);
                        toast('Error updating sales rep', 'error');
                      });
                  }}
                />
              )}

              <DefaultInvoiceFreeText
                defaultInvoiceFreeText={order.defaultInvoiceFreeText}
                updateDefaultInvoiceFreeText={(defaultInvoiceFreeText: string) =>
                  updateCustomContractInvoiceText(order.id, defaultInvoiceFreeText, order.rentalDisclaimer ?? '').then(
                    () => setOrder({ ...order, defaultInvoiceFreeText }),
                  )
                }
              />
              <DefaultInvoiceFreeText
                defaultInvoiceFreeText={order.rentalDisclaimer}
                updateDefaultInvoiceFreeText={(text: string) =>
                  updateCustomContractInvoiceText(order.id, order.defaultInvoiceFreeText ?? '', text).then(() =>
                    setOrder({ ...order, rentalDisclaimer: text }),
                  )
                }
                isRentalDisclaimer={true}
              />

              <OrderMediaList order={order} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const OrderDetails = ({
  order,
  isQuote,
  location,
  author,
  isDisabled,
  refresh,
}: {
  order: Order;
  isQuote: boolean;
  location: Location;
  author?: Admin;
  isDisabled: boolean;
  refresh: () => void;
}) => {
  const { approveAccount, updateOrderPayment, getConfiguration } = useAPI();
  const { captureCard } = usePaymentApiV2();
  const admin = useAdmin();

  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [columns, setColumns] = useState<DataTableColumn[]>([]);
  const [isKilometers, setIsKilometers] = useState<boolean>();
  const [paymentType, setPaymentType] = useState<OrderPaymentMethod>(order.paymentMethodType);
  const [cardId, setCardId] = useState<number>();
  const [card, setCard] = useState<StripeCard>();
  const [bankAccountId, setBankAccountId] = useState<string>();
  const [onAccount, setOnAccount] = useState<OnAccount>();
  const [confirmationTokenId, setConfirmationTokenId] = useState<string>();
  const [poNumber, setPoNumber] = useState<string>(order.poNumber ?? '');
  const [customPaymentMethod, setCustomPaymentMethod] = useState<string>();

  const createdByColumn = { key: 'author', label: 'Order Created By' };

  const PaymentMethodFormat = () => {
    const method = order.paymentMethod;

    switch (method.type) {
      case PaymentMethodType.Custom: {
        return (
          <Typography type="text-sm" className="flex items-center gap-2" weight="semi-bold">
            <>
              <span>
                {method.name}
                {method.memo ? ` - ${method.memo}` : ''}
              </span>
            </>
          </Typography>
        );
      }

      case PaymentMethodType.Stripe: {
        if (method.bankAccount) {
          return (
            <Typography type="text-sm" className="flex items-center gap-2" weight="semi-bold">
              <>
                <span> Bank Account •••• {method.bankAccount?.last4}</span>
              </>
            </Typography>
          );
        } else {
          let icon = undefined;
          switch (method.card?.brand.toLowerCase().trim()) {
            case 'visa':
              icon = <Icon icon={'VisaCardLogo'} size={'small'}></Icon>;
              break;
            case 'mastercard':
              icon = <Icon icon={'MasterCardLogo'} size={'small'}></Icon>;
              break;
            case 'amex':
            case 'americanexpress':
              icon = <Icon icon={'AmericanExpressLogo'} size={'small'}></Icon>;
              break;
          }
          return (
            <Typography type="text-sm" className="flex items-center gap-2" weight="semi-bold">
              {method.method === 'Affirm' ? (
                <>Affirm</>
              ) : (
                <>
                  <span>•••• {method.card?.last4}</span>
                  {icon}
                </>
              )}
            </Typography>
          );
        }
      }
      case PaymentMethodType.OnAccount:
        return (
          <>
            <Typography className="text-quipli-black-800" type="text-sm">
              On Account
            </Typography>

            <Typography className="text-quipli-black-800" type="text-sm" weight="semi-bold">
              ({method.account.accountNumber})
            </Typography>

            {method.account.status !== OnAccountStatus.Approved && (
              <>
                <Badge
                  text={method.account.status}
                  type={method.account.status === OnAccountStatus.Declined ? 'danger' : 'default'}
                />

                <Button
                  className="button-primary"
                  dataCy="approve-order"
                  disabled={isLoading || isDisabled}
                  onClick={() => {
                    if (isLoading) return;

                    setIsLoading(true);
                    approveAccount(order.user.id, admin.id)
                      .then(() => {
                        setIsLoading(false);
                        refresh();
                        toast('Account approved.');
                      })
                      .catch((e) => {
                        toast(e.message, 'error');
                      })
                      .finally(() => {
                        setIsLoading(false);
                      });
                  }}
                >
                  Approve
                </Button>
              </>
            )}
          </>
        );
      default:
        return <Typography type="text-sm">{method.type}</Typography>;
    }
  };

  const GoogleShippingTypeFormat = () => {
    if (order.googleShippingCalculation === undefined) return null;

    switch (order.googleShippingCalculation.type) {
      case 'Distance':
        return (
          <Typography type="text-sm" className="text-quipli-black-800">{`Per ${
            isKilometers ? 'KM' : 'Miles'
          }`}</Typography>
        );
      case 'Duration':
        return (
          <Typography type="text-sm" className="text-quipli-black-800">
            Per Minute
          </Typography>
        );
    }
  };

  const handleSave = async () => {
    // Check if required PO number
    const customer = order.user;
    if (customer && customer.requirePoNum && (!poNumber || poNumber === '')) {
      toast('PO Number is required.', 'error');
      return;
    }

    setIsLoading(true);

    const _cardId = cardId;
    let paymentMethod: PaymentMethodRequest;
    if (confirmationTokenId) {
      try {
        setIsLoading(true);
        const _card = await captureCard({ confirmationTokenId, userId: order!.user.id });
        const _cardId = _card.id;
        setConfirmationTokenId(undefined);
        setCardId(_cardId);
        setCard(card);
        toast('Card captured successfully.', 'success');
      } catch (error: any) {
        toast(error.message || 'Something went wrong.', 'error');
      } finally {
        setIsLoading(false);
      }
    }
    switch (paymentType) {
      case OrderPaymentMethod.BankAccount:
        paymentMethod = { type: PaymentMethodType.Stripe, bankAccountId, poNumber } as StripePaymentMethodRequest;
        break;
      case OrderPaymentMethod.CreditCard:
        paymentMethod = { type: PaymentMethodType.Stripe, cardId: _cardId, poNumber } as StripePaymentMethodRequest;
        break;
      case OrderPaymentMethod.OnAccount:
        paymentMethod = {
          type: PaymentMethodType.OnAccount,
          accountNumber: '',
          poNumber,
        } as OnAccountPaymentMethodRequest;
        break;
      case OrderPaymentMethod.Cash:
        paymentMethod = { type: PaymentMethodType.Cash, poNumber } as CashPaymentMethod;
        break;
      case OrderPaymentMethod.Check:
        paymentMethod = { type: PaymentMethodType.Check, poNumber } as CheckPaymentMethod;
        break;
      case OrderPaymentMethod.Custom:
        paymentMethod = {
          type: PaymentMethodType.Custom,
          name: customPaymentMethod,
          poNumber,
        } as CustomPaymentMethod;
        break;
      case OrderPaymentMethod.ProceedWithoutPayment:
      default:
        paymentMethod = { type: PaymentMethodType.None, poNumber };
        break;
    }

    try {
      await updateOrderPayment(order.id, paymentMethod);
      toast('Payment updated successfully.');
      refresh();
      setIsOpen(false);
    } catch (ex: any) {
      toast(typeof ex.message === 'string' ? ex.message : 'Something went wrong.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    const fetchAll = async () => {
      setIsDataLoading(true);

      const additionalColumns: DataTableColumn[] = [
        { key: 'invoiceId', label: 'Invoice Id', showRule: 'OnlyDesktop' },
        { key: 'paymentMethod', label: 'Payment Method' },
      ];

      if (order.poNumber) {
        additionalColumns.push({ key: 'poNumber', label: 'PO Number' });
      }

      additionalColumns.push({ key: 'date', label: 'Order Date' }, { key: 'location', label: 'Location' });

      if (author && !columns.includes(createdByColumn)) {
        additionalColumns.push(createdByColumn);
      }

      if (
        order.googleShippingCalculation !== undefined &&
        order.products.some((p) => isDeliveryTripRequired(p.shippingType))
      ) {
        const showKilometers = (await getConfiguration('CONF_CALCULATE_DELIVERY_USING_KM_GOOGLE_API'))?.value === '1';
        setIsKilometers(showKilometers);

        additionalColumns.push(
          {
            key: 'googleShipping',
            label: 'Google Shipping',
          },
          {
            key: 'googleShippingValue',
            label: order.googleShippingCalculation.type === 'Distance' ? 'Distance' : 'Time',
          },
        );
      }

      setColumns([...additionalColumns, { key: 'editPayment', showRule: 'OnlyMobile' }]);
      setIsDataLoading(false);
    };

    fetchAll();
  }, [order]);

  return (
    <>
      <OrderSection className="flex flex-col gap-[8px]" header={`View ${isQuote ? 'Quote' : 'Order'}`}>
        {isDataLoading ? (
          <Spinner />
        ) : (
          <div
            className={classNames({
              'border border-solid border-grey-200 rounded-[9px]': !isMobileOnly,
            })}
          >
            <DataTable
              columnNames={columns}
              data={[
                {
                  mobileHeader: (
                    <>
                      <Typography type="text-md" weight="medium">
                        Invoice Id
                      </Typography>
                      <Typography type="text-sm" weight="regular" className="text-grey-500">
                        {order.orderNumber}
                      </Typography>
                    </>
                  ),
                  className: 'py-[16px]',
                  fields: {
                    invoiceId: order.orderNumber,
                    paymentMethod: (
                      <div className="flex items-center gap-[8px]">
                        <PaymentMethodFormat />

                        {!isMobileOnly && (
                          <Button
                            className="button-secondary"
                            onClick={() => setIsOpen(true)}
                            disabled={isLoading || isDisabled}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    ),
                    poNumber: order.poNumber || 'N/A',
                    location: location.name,
                    date: (
                      <div className="flex flex-col">
                        <Typography type="text-sm" className="text-quipli-black-800">
                          {order.dateAdded.toFormat('DD')}
                        </Typography>
                        <Typography type="text-sm" className="text-grey-500">
                          {order.dateAdded.toFormat('t')}
                        </Typography>
                      </div>
                    ),
                    author: (
                      <Typography type="text-sm" className="capitalize">
                        {author?.adminFullName || 'N/A'}
                      </Typography>
                    ),
                    googleShipping: <GoogleShippingTypeFormat />,
                    googleShippingValue: (
                      <GoogleShippingValueFormat
                        order={order}
                        isKilometers={isKilometers ?? false}
                        className="text-quipli-black-800"
                      />
                    ),
                    editPayment: (
                      <Button
                        className="button-secondary w-full"
                        onClick={() => setIsOpen(true)}
                        disabled={isLoading || isDisabled}
                      >
                        <Typography type="text-md" weight="medium">
                          Edit Payment
                        </Typography>
                      </Button>
                    ),
                  },
                },
              ]}
            />
          </div>
        )}
      </OrderSection>

      <FiltersModal
        isOpen={isOpen}
        showConfirmButtonFirst={false}
        title="Select Payment Method"
        confirmText="Save"
        cancelText="Close"
        onCancel={() => {
          setIsOpen(false);
          setPoNumber(order.poNumber ?? '');
        }}
        allowConfirm={
          !(paymentType == OrderPaymentMethod.Terminal || (paymentType == OrderPaymentMethod.OnAccount && !onAccount))
        }
        onConfirm={handleSave}
      >
        <div className="max-w-md flex flex-col gap-[12px]">
          <PaymentContextProvider>
            <OrderPaymentMethodSelector
              customer={order.user}
              location={location}
              poNumber={order.poNumber}
              allowedPaymentMethods={[
                OrderPaymentMethod.CreditCard,
                OrderPaymentMethod.Terminal,
                OrderPaymentMethod.OnAccount,
                OrderPaymentMethod.Cash,
                OrderPaymentMethod.Check,
              ]}
              paymentMethod={paymentType}
              setPaymentMethod={setPaymentType}
              setCardId={setCardId}
              setPoNumber={setPoNumber}
              setBankAccountId={setBankAccountId}
              setOnAccount={setOnAccount}
              setCustomPaymentMethod={setCustomPaymentMethod}
              customPaymentMethod={customPaymentMethod}
              setCard={(card) => {
                if (card.confirmationTokenId) {
                  setConfirmationTokenId(card.confirmationTokenId);
                } else {
                  setConfirmationTokenId(undefined);
                }
                setCard(card);
              }}
              card={card}
              zIndex={2002}
            />
          </PaymentContextProvider>
        </div>
      </FiltersModal>
    </>
  );
};

export const GoogleShippingValueFormat = (props: { order: Order; isKilometers: boolean; className: string }) => {
  const { order, isKilometers, className = '' } = props;
  if (order.googleShippingCalculation === undefined) return null;

  switch (order.googleShippingCalculation.type) {
    case 'Distance': {
      const value = (order.googleShippingCalculation.distance / (isKilometers ? 1000 : 1609.34)).toFixed(1);
      return (
        <Typography type="text-sm" className={className}>
          {value} {isKilometers ? 'km' : 'miles'}
        </Typography>
      );
    }
    case 'Duration':
      return (
        <Typography type="text-sm" className={className}>
          {order.googleShippingCalculation.duration}
        </Typography>
      );
  }
};

const OrderCycleBillingDetails = ({ order, reload }: { order: Order; reload: () => void }) => {
  const { updateOrderCycleBillingTerms, updateAutoPay } = useOrderApiV1();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAutoCharged, setIsAutoCharged] = useState<boolean>(order.isAutoCharged);
  const [paymentPeriod, setPaymentPeriod] = useState<Duration>(order.cycleBillingTerms!.paymentPeriod);

  const handleToggleAutoPay = async (toggle: boolean) => {
    setIsLoading(true);

    try {
      const { status, msg } = await updateAutoPay(order.id, toggle);

      if (status) {
        setIsAutoCharged(toggle);
        toast(`Automatic payments ${toggle ? 'enabled' : 'disabled'}.`);
      } else {
        toast(msg, 'error');
      }
    } catch (ex: any) {
      toast(typeof ex.message === 'string' ? ex.message : 'Something went wrong.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCycleBillingTerms = async () => {
    setIsSubmitting(true);

    try {
      await updateOrderCycleBillingTerms(order.id, { paymentPeriod });

      toast('Cycle billing terms updated successfully.');
      reload();
      setIsEditing(false);
    } catch (ex: any) {
      toast(typeof ex.message === 'string' ? ex.message : 'Something went wrong.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return order.cycleBillingTerms ? (
    <OrderSection
      className="flex flex-col gap-[8px]"
      header={
        <div className="flex justify-between">
          <Typography type="text-md" weight="medium" className="text-grey-800">
            Cycle Billing Details
          </Typography>

          <div
            className={classNames({
              'cursor-pointer': !isEditing,
              'cursor-not-allowed': isEditing,
            })}
            onClick={() => !isEditing && setIsEditing(true)}
          >
            <Icon icon="Edit" stroke={isEditing ? 'var(--grey-200)' : undefined} />
          </div>
        </div>
      }
    >
      <div
        className={classNames({
          'border border-solid border-grey-200 rounded-[9px]': !isMobileOnly,
        })}
      >
        <DataTable
          columnNames={[
            { label: 'Cycle Length', key: 'length', showRule: 'OnlyDesktop' },
            { label: 'Invoice Date', key: 'date' },
            { label: 'Payment Period', key: 'paymentPeriod' },
            { label: 'Automatic Payments', key: 'automaticPayment' },
          ]}
          data={[
            {
              mobileHeader: (
                <>
                  <Typography type="text-md" weight="medium">
                    Cycle Length
                  </Typography>
                  <Typography type="text-sm" weight="regular" className="text-grey-500">
                    {order.cycleBillingTerms.cycleLength.toHuman()}
                  </Typography>
                </>
              ),
              className: 'py-[16px]',
              fields: {
                length: order.cycleBillingTerms.cycleLength.toHuman(),
                date: `${addNumericSuffix(order.cycleBillingTerms.cycleInvoiceDay)} ${
                  order.cycleBillingTerms.cycleLength.months === 1 && order.cycleBillingTerms.isDayOfMonth
                    ? 'of month'
                    : 'day'
                }`,
                paymentPeriod: isEditing ? (
                  <Field
                    type="number"
                    placeholder="30"
                    isRequired={true}
                    value={paymentPeriod.days}
                    onChange={(newValue) => setPaymentPeriod(Duration.fromObject({ days: newValue }))}
                  />
                ) : order.cycleBillingTerms.paymentPeriod.days === 0 ? (
                  'Same Day'
                ) : (
                  order.cycleBillingTerms.paymentPeriod.toHuman()
                ),
                automaticPayment: (
                  <Checkbox
                    label={isAutoCharged ? 'Enabled' : 'Disabled'}
                    isChecked={isAutoCharged}
                    onChange={handleToggleAutoPay}
                    isDisabled={order.paymentMethodType !== OrderPaymentMethod.CreditCard || isLoading || isEditing}
                  />
                ),
              },
            },
          ]}
        />
      </div>

      {isEditing && (
        <div className="button-group">
          <Button className="button-secondary" disabled={isSubmitting} onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button className="button-primary" disabled={isSubmitting} onClick={handleUpdateCycleBillingTerms}>
            Save
          </Button>
        </div>
      )}
    </OrderSection>
  ) : null;
};

const OrderStatusView = ({ order, location, isQuote }: { order: Order; location: Location; isQuote: boolean }) => {
  const StatusItem = ({ text, tooltip, isActive }: { text: string; tooltip?: string; isActive: boolean }) => (
    <div
      className={classNames([styles.orderTracking, styles.orderSerialsTracking], {
        [styles.completed]: isActive && ![OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status),
        [styles.cancelled]: order.status === OrderStatus.Cancelled,
        [styles.missed]: order.status === OrderStatus.Missed,
      })}
    >
      <Tooltip tooltip={`${text}\n${tooltip}`} type="black" invisible={(tooltip?.length || 0) === 0} allowBreakpoints>
        <span className={classNames(styles.isComplete)}></span>
      </Tooltip>

      <div className="mt-[16px]">
        <Typography
          type="text-sm"
          weight={isActive ? 'medium' : 'regular'}
          className={classNames({
            'text-quipli-black-800': isActive,
            'text-grey-300': !isActive,
          })}
        >
          {text}
        </Typography>
      </div>
    </div>
  );

  const rentalProducts = order.products.filter((op) => op.type === OrderType.Rental);
  const StatusTimeline = () => (
    <div className="flex justify-between py-[14px]">
      <StatusItem
        text={order.status === OrderStatus.Requested ? 'Request' : 'Reservation'}
        tooltip={
          order.status === OrderStatus.Quote && order.source === OrderSource.Quote
            ? undefined
            : order.dateAdded.toFormat('ff')
        }
        isActive={![OrderStatus.Requested, OrderStatus.Quote].includes(order.status)}
      />

      <StatusItem
        text={'On Rent'}
        tooltip={
          rentalProducts.some(
            (op) =>
              op.status &&
              [OrderReturnStatus.OnRent, OrderReturnStatus.OffRent, OrderReturnStatus.Complete].includes(op.status),
          )
            ? DateTime.min(
                ...order.products
                  .filter(({ type }) => type === OrderType.Rental)
                  .map((p) => [p.rentalStartDate, ...(p.actualRentalStartDate ? [p.actualRentalStartDate] : [])])
                  .flat(),
              ).toFormat('ff')
            : undefined
        }
        isActive={
          order.status !== OrderStatus.Requested &&
          rentalProducts.some(
            (op) =>
              op.status &&
              [OrderReturnStatus.OnRent, OrderReturnStatus.OffRent, OrderReturnStatus.Complete].includes(op.status),
          )
        }
      />

      <StatusItem
        text="Off Rent"
        tooltip={
          rentalProducts.length > 0 &&
          rentalProducts.every(
            (op) => op.status && ![OrderReturnStatus.NotStarted, OrderReturnStatus.OnRent].includes(op.status),
          )
            ? DateTime.max(
                ...order.products
                  .map((p) => [
                    ...(p.rentalEndDate ? [p.rentalEndDate] : []),
                    ...(p.actualRentalEndDate ? [p.actualRentalEndDate] : []),
                  ])
                  .flat(),
              ).toFormat('ff')
            : undefined
        }
        isActive={
          order.status !== OrderStatus.Requested &&
          rentalProducts.length > 0 &&
          rentalProducts.every(
            (op) => op.status && ![OrderReturnStatus.NotStarted, OrderReturnStatus.OnRent].includes(op.status),
          )
        }
      />

      <StatusItem
        text="Return"
        tooltip={
          rentalProducts.length > 0 && rentalProducts.every((op) => op.status === OrderReturnStatus.Complete)
            ? DateTime.max(
                ...(order.returnDate ? [order.returnDate] : []),
                ...order.products.map((p) => [...(p.inventoryReturnedAt ? [p.inventoryReturnedAt] : [])]).flat(),
              ).toFormat('ff')
            : undefined
        }
        isActive={
          order.status !== OrderStatus.Requested &&
          rentalProducts.length > 0 &&
          rentalProducts.every((op) => op.status === OrderReturnStatus.Complete)
        }
      />
    </div>
  );

  return (
    <OrderSection>
      <Collapsible
        openAtStart={order.type === OrderType.Sale || order.documentType == OrderDocumentType.CreditMemo}
        showCursor={order.statusHistory.length > 0}
        trigger={(isOpen) => (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {isQuote ? 'Quote' : 'Order'} Status
            </Typography>

            <div className="flex gap-[8px] items-center">
              <Typography type="text-sm" className="text-grey-600">
                {isOpen ? 'Hide' : 'Show'} Event History
              </Typography>
              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" stroke="var(--grey-500)" />
            </div>
          </div>
        )}
        beforeHiddenContent={
          order.type === OrderType.Rental && order.documentType !== OrderDocumentType.CreditMemo && <StatusTimeline />
        }
        afterHiddenContent={
          <div className="float-right">
            <UserAgree order={order} />
          </div>
        }
      >
        <OrderHistory order={order} location={location} />
      </Collapsible>
    </OrderSection>
  );
};

export const AssignSalesRep = ({
  salesRep,
  updateSalesRep,
}: {
  salesRep?: number;
  updateSalesRep: (rep?: number) => void;
}) => {
  const { getAdminsList } = useAdminApiV1();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState<Admin | undefined>();

  useEffect(() => {
    const init = async () => {
      if (!admins.length) {
        const _admins = await getAdminsList();
        setAdmins(_admins);
        setSelectedSalesRep(_admins.find((a) => a.adminId == salesRep) ?? undefined);
      } else {
        setSelectedSalesRep(admins.find((a) => a.adminId == salesRep) ?? undefined);
      }
    };
    init();
  }, [salesRep]);

  return (
    <OrderSection header="Sales Representative">
      <SelectForm
        className="pt-[8px]"
        placeholder="Select sales rep"
        isSearchable={true}
        value={selectedSalesRep}
        options={admins}
        getOptionLabel={(a) => a.adminFullName}
        onChange={(a) => {
          updateSalesRep(a?.adminId);
          setSelectedSalesRep(admins.find((ad) => ad.adminId == a?.adminId) ?? undefined);
        }}
        isClearable={true}
      />
    </OrderSection>
  );
};
